import { UserManager, UserManagerSettings, User } from 'oidc-client';

import {
    IAuthService,
    IAuthUser,
    IAuthEvents,
    AuthEventCallback,
} from './IAuthService';

function userToAuthUser(user: User): IAuthUser {
    return {
        access_token: user.access_token,
        id_token: user.id_token,
        profile: {
            sub: user.profile.sub,
            email: user.profile.email,
            given_name: user.profile.given_name,
            family_name: user.profile.family_name,
            preferred_username: user.profile.preferred_username,
            groups: user.profile.groups as string,
        },
        expired: user.expired,
        expires_at: user.expires_at,
    };
}

class Wso2AuthService implements IAuthService {
    private readonly userManager: UserManager;

    constructor(settings: UserManagerSettings) {
        this.userManager = new UserManager(settings);
    }

    public get events(): IAuthEvents {
        return {
            addSilentRenewError: (callback: AuthEventCallback) => {
                this.userManager.events.addSilentRenewError(callback as (error: Error) => void);
            },
            addUserSignedOut: (callback: AuthEventCallback) => {
                this.userManager.events.addUserSignedOut(callback as () => void);
            },
            addUserSessionChanged: (callback: AuthEventCallback) => {
                this.userManager.events.addUserSessionChanged(callback as () => void);
            },
            addUserLoaded: (callback: AuthEventCallback) => {
                this.userManager.events.addUserLoaded(callback as (user: User) => void);
            },
            removeSilentRenewError: (callback: AuthEventCallback) => {
                this.userManager.events.removeSilentRenewError(callback as (error: Error) => void);
            },
            removeUserSignedOut: (callback: AuthEventCallback) => {
                this.userManager.events.removeUserSignedOut(callback as () => void);
            },
            removeUserSessionChanged: (callback: AuthEventCallback) => {
                this.userManager.events.removeUserSessionChanged(callback as () => void);
            },
            removeUserLoaded: (callback: AuthEventCallback) => {
                this.userManager.events.removeUserLoaded(callback as (user: User) => void);
            },
        };
    }

    public async login(): Promise<void> {
        await this.userManager.signinRedirect();
    }

    public async getUser(): Promise<IAuthUser | null> {
        const user = await this.userManager.getUser();
        return user ? userToAuthUser(user) : null;
    }

    public async refreshToken(): Promise<IAuthUser> {
        const user = await this.userManager.signinSilent();
        return userToAuthUser(user);
    }

    public async logout(): Promise<void> {
        await this.userManager.signoutRedirect();
    }

    public async removeUser(): Promise<void> {
        await this.userManager.removeUser();
    }

    public async clearStaleState(): Promise<void> {
        await this.userManager.clearStaleState();
    }

    public async revokeAccessToken(): Promise<void> {
        await this.userManager.revokeAccessToken();
    }

    public async handleRedirectCallback(): Promise<IAuthUser> {
        const user = await this.userManager.signinRedirectCallback();
        return userToAuthUser(user);
    }

    public async handleLogoutCallback(): Promise<void> {
        await this.userManager.signoutRedirectCallback();
    }
}

export default Wso2AuthService;
